<script lang="ts">
  import { createEventDispatcher } from 'svelte';
  import { setEventTicketing } from '../../lib/convexApi';
  import type { TicketTier } from '../../lib/types';
  import Notice from '../ui/Notice.svelte';

  // Structural prop: works for both CommunityEventSummary (settings panel)
  // and ManagedEventConfig (manage-area Events tab).
  export let event: {
    id: string;
    ticketing?: { capacity: number | null; tiers: TicketTier[] } | null;
  };

  const dispatch = createEventDispatcher<{ changed: void }>();

  type TierDraft = {
    id: string;
    name: string;
    description: string;
    capacity: number | null; // null = unlimited
    price: number;
    isDynamic: boolean;
    endPrice: number;
    dynamicStart: string;
    dynamicEnd: string;
    active: boolean;
    salesStart: string;
    salesEnd: string;
  };

  function makeId(name: string): string {
    const slug = name
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
    const suffix = Math.random().toString(36).slice(2, 6);
    return slug ? `${slug}-${suffix}` : `tier-${suffix}`;
  }

  function toLocalInput(value: string | null): string {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
  }

  function fromLocalInput(value: string): string | null {
    if (!value) return null;
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date.toISOString();
  }

  function toDraft(tier: TicketTier): TierDraft {
    return {
      id: tier.id,
      name: tier.name,
      description: tier.description ?? '',
      capacity: tier.capacity === -1 ? null : tier.capacity,
      price: tier.price,
      isDynamic: Boolean(tier.dynamic),
      endPrice: tier.dynamic?.endPrice ?? tier.price,
      dynamicStart: toLocalInput(tier.dynamic?.start ?? null),
      dynamicEnd: toLocalInput(tier.dynamic?.end ?? null),
      active: tier.active !== false,
      salesStart: toLocalInput(tier.salesStart ?? null),
      salesEnd: toLocalInput(tier.salesEnd ?? null)
    };
  }

  let capacity: number | null = event.ticketing?.capacity ?? null;
  let tiers: TierDraft[] = (event.ticketing?.tiers ?? []).map(toDraft);
  let saving = false;
  let error: string | null = null;
  let saved = false;

  function addTier() {
    tiers = [
      ...tiers,
      {
        id: '',
        name: '',
        description: '',
        capacity: null,
        price: 0,
        isDynamic: false,
        endPrice: 0,
        dynamicStart: '',
        dynamicEnd: '',
        active: true,
        salesStart: '',
        salesEnd: ''
      }
    ];
    saved = false;
  }

  function removeTier(index: number) {
    const tier = tiers[index];
    if (tier?.name && !window.confirm(`Remove "${tier.name}"? Tickets already sold keep it.`)) {
      return;
    }
    tiers = tiers.filter((_, i) => i !== index);
    saved = false;
  }

  function onCapacityInput(domEvent: Event) {
    const raw = (domEvent.target as HTMLInputElement).value;
    capacity = raw === '' ? null : Number(raw);
    saved = false;
  }

  function onTierCapacityInput(index: number) {
    return (domEvent: Event) => {
      const raw = (domEvent.target as HTMLInputElement).value;
      tiers[index].capacity = raw === '' ? null : Number(raw);
      tiers = [...tiers];
      saved = false;
    };
  }

  async function handleSave() {
    error = null;
    for (const [index, tier] of tiers.entries()) {
      if (!tier.name.trim()) {
        error = `Tier ${index + 1} needs a name.`;
        return;
      }
      if (!Number.isFinite(tier.price) || tier.price < 0) {
        error = `Tier ${index + 1}: price must be 0 or more.`;
        return;
      }
      if (tier.isDynamic && (!Number.isFinite(tier.endPrice) || tier.endPrice < 0)) {
        error = `Tier ${index + 1}: end price must be 0 or more.`;
        return;
      }
    }

    const payloadTiers: TicketTier[] = tiers.map(tier => ({
      id: tier.id || makeId(tier.name),
      name: tier.name.trim(),
      description: tier.description.trim() || null,
      capacity: tier.capacity === null ? -1 : Math.floor(tier.capacity),
      price: tier.price,
      dynamic: tier.isDynamic
        ? {
            endPrice: tier.endPrice,
            start: fromLocalInput(tier.dynamicStart),
            end: fromLocalInput(tier.dynamicEnd)
          }
        : null,
      active: tier.active,
      salesStart: fromLocalInput(tier.salesStart),
      salesEnd: fromLocalInput(tier.salesEnd)
    }));

    saving = true;
    try {
      await setEventTicketing({
        eventId: event.id,
        capacity: capacity === null ? null : Math.floor(capacity),
        tiers: payloadTiers.length > 0 ? payloadTiers : null
      });
      // Persist generated ids locally so a rename doesn't mint a new tier.
      tiers = payloadTiers.map(toDraft);
      saved = true;
      dispatch('changed');
    } catch (err) {
      error = err instanceof Error ? err.message : 'Could not save ticketing.';
    } finally {
      saving = false;
    }
  }
</script>

<div class="ticketing">
  {#if error}
    <Notice variant="error">{error}</Notice>
  {:else if saved}
    <Notice variant="success">Ticketing saved.</Notice>
  {/if}

  <label class="ticketing__capacity">
    Event capacity (paid tickets)
    <input
      type="number"
      min="1"
      placeholder="Unlimited"
      value={capacity ?? ''}
      on:input={onCapacityInput}
    />
    <span class="ticketing__hint">Lowering below tickets already sold only stops future sales.</span>
  </label>

  {#each tiers as tier, index (tier.id || index)}
    <div class="ticketing__tier">
      <div class="ticketing__tier-head">
        <span class="ticketing__tier-label">Tier {index + 1}</span>
        <button type="button" class="ghost" on:click={() => removeTier(index)} aria-label="Remove tier">✕</button>
      </div>
      <div class="ticketing__row">
        <label>
          Name
          <input type="text" bind:value={tier.name} placeholder="Early bird" on:input={() => (saved = false)} />
        </label>
        <label>
          Capacity
          <input
            type="number"
            min="1"
            placeholder="Unlimited"
            value={tier.capacity ?? ''}
            on:input={onTierCapacityInput(index)}
          />
        </label>
      </div>
      <label>
        Description (optional)
        <input type="text" bind:value={tier.description} on:input={() => (saved = false)} />
      </label>
      <div class="ticketing__row">
        <label>
          {tier.isDynamic ? 'Start price' : 'Price'}
          <input type="number" min="0" step="0.01" bind:value={tier.price} on:input={() => (saved = false)} />
        </label>
        <label class="ticketing__toggle">
          <input type="checkbox" bind:checked={tier.isDynamic} on:change={() => (saved = false)} />
          Price increases over time
        </label>
      </div>
      {#if tier.isDynamic}
        <div class="ticketing__row">
          <label>
            End price
            <input type="number" min="0" step="0.01" bind:value={tier.endPrice} on:input={() => (saved = false)} />
          </label>
          <label>
            Increase starts
            <input type="datetime-local" bind:value={tier.dynamicStart} on:input={() => (saved = false)} />
          </label>
          <label>
            Increase ends
            <input type="datetime-local" bind:value={tier.dynamicEnd} on:input={() => (saved = false)} />
          </label>
        </div>
      {/if}
      <div class="ticketing__row">
        <label class="ticketing__toggle">
          <input type="checkbox" bind:checked={tier.active} on:change={() => (saved = false)} />
          On sale
        </label>
        <label>
          Sale opens (optional)
          <input type="datetime-local" bind:value={tier.salesStart} on:input={() => (saved = false)} />
        </label>
        <label>
          Sale closes (optional)
          <input type="datetime-local" bind:value={tier.salesEnd} on:input={() => (saved = false)} />
        </label>
      </div>
      {#if !tier.active}
        <span class="ticketing__off">This ticket is switched off — buyers can't select it.</span>
      {/if}
    </div>
  {/each}

  <div class="ticketing__actions">
    <button type="button" class="secondary" on:click={addTier}>Add tier</button>
    <button type="button" class="accent" on:click={handleSave} disabled={saving}>
      {saving ? 'Saving…' : 'Save ticketing'}
    </button>
  </div>
</div>

<style>
  .ticketing {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
    padding-top: var(--space-2);
  }

  .ticketing label {
    display: flex;
    flex-direction: column;
    gap: var(--space-1);
    font-size: var(--font-size-xs);
    color: var(--color-text-muted);
    flex: 1;
    min-width: 140px;
  }

  .ticketing input[type='text'],
  .ticketing input[type='number'],
  .ticketing input[type='datetime-local'] {
    padding: var(--space-2) var(--space-3);
    border-radius: var(--radius-sm);
    border: var(--border-subtle);
    background: var(--color-surface-900);
    color: var(--color-text-primary);
    font-family: var(--font-sans);
    font-size: var(--font-size-sm);
  }

  .ticketing__capacity {
    max-width: 260px;
  }

  .ticketing__hint {
    font-size: var(--font-size-2xs);
    color: var(--color-text-muted);
  }

  .ticketing__tier {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
    padding: var(--space-3);
    border-radius: var(--radius-md);
    border: var(--border-subtle);
    background: var(--color-surface-900);
  }

  .ticketing__tier-head {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .ticketing__tier-label {
    font-size: var(--font-size-2xs);
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: var(--color-text-amber);
  }

  .ticketing__tier-head button {
    padding: var(--space-1) var(--space-2);
    font-size: var(--font-size-xs);
  }

  .ticketing__row {
    display: flex;
    gap: var(--space-2);
    flex-wrap: wrap;
    align-items: flex-end;
  }

  .ticketing__toggle {
    flex-direction: row !important;
    align-items: center;
    gap: var(--space-2) !important;
    font-size: var(--font-size-sm) !important;
    color: var(--color-text-secondary) !important;
    padding-bottom: var(--space-2);
  }

  .ticketing__off {
    font-size: var(--font-size-2xs);
    color: var(--color-text-amber);
  }

  .ticketing__actions {
    display: flex;
    justify-content: space-between;
    gap: var(--space-2);
  }
</style>
